import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { studentService, ArtworkResponse } from '../services';
import type { ApiError } from '../services/api';
import { ArtworkGrid } from '../components/artwork';
import { LoadingSpinner, ErrorBoundary, InlineError, ServiceOutageIndicator, RetryableError, ArtworkLightbox } from '../components/ui';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  }
`;

const Breadcrumb = styled.nav`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const BreadcrumbList = styled.ol`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  min-width: 0;
  overflow-wrap: anywhere;
`;

const BreadcrumbItem = styled.li`
  display: flex;
  min-width: 0;
  align-items: center;
  color: ${({ theme }) => theme.colors.gray.dark};

  &:not(:last-child)::after {
    content: '›';
    margin-left: ${({ theme }) => theme.spacing.sm};
    color: ${({ theme }) => theme.colors.gray.medium};
    font-weight: bold;
  }
`;

const BreadcrumbLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.secondary};
    text-decoration: underline;
  }
`;

const BreadcrumbCurrent = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const StudentInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const StudentDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const StudentName = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  overflow-wrap: anywhere;
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  }
`;

const StudentSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.gray.dark};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StudentStats = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.gray.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};

`;

const ArtworkCountStat = styled(StatItem)`
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.white};
  border: 2px solid ${({ theme }) => theme.colors.gray.medium};
  color: ${({ theme }) => theme.colors.text};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.gray.light};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  &::before {
    content: '←';
    font-weight: bold;
  }
`;

const GallerySection = styled.section`
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const ErrorContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const PortfolioPausedState = styled.div`
  display: grid;
  justify-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing['3xl']} ${({ theme }) => theme.spacing.lg};
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.gallery.surface};
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  text-align: center;
`;

const PortfolioRetryButton = styled.button`
  min-height: 44px;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  border: 1px solid ${({ theme }) => theme.colors.gallery.accent.border};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.gallery.accent.primary};
  color: ${({ theme }) => theme.colors.gallery.text.inverse};
  font: inherit;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.gallery.accent.hover};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 3px;
  }
`;

const MAX_STUDENT_ID = 2_147_483_647;

export interface StudentArtworkPageProps {
  galleryMode?: boolean;
}

interface StudentArtworkLocationState {
  fromArtistsDirectory?: boolean;
}

export function StudentArtworkPage({ galleryMode = true }: StudentArtworkPageProps = {}) {
  const { studentId } = useParams<{ studentId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState<ArtworkResponse | null>(null);
  const [currentArtworkIndex, setCurrentArtworkIndex] = useState(0);
  const [artworksForLightbox, setArtworksForLightbox] = useState<ArtworkResponse[]>([]);
  const [lightboxRouteId, setLightboxRouteId] = useState<number | null>(null);

  const studentIdNum = studentId && /^[1-9]\d*$/.test(studentId) && Number.isSafeInteger(Number(studentId)) && Number(studentId) <= MAX_STUDENT_ID
    ? Number(studentId)
    : null;

  const lightboxOpenerRef = useRef<HTMLElement | null>(null);

  // Fetch student details
  const studentQueryState = useQuery({
    queryKey: ['student', studentIdNum, 'public'],
    queryFn: ({ signal }) => studentService.getPublicStudent(studentIdNum!, signal),
    enabled: studentIdNum !== null,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
    retry: false,
    networkMode: 'always',
  });

  const {
    data: student,
    isLoading: isLoadingStudent,
    isError: isStudentError,
    error: studentError,
    refetch: refetchStudent,
  } = studentQueryState;

  // Fetch artworks for this student
  const studentArtworkPageSize = 20;
  const publicStudentVerified = Boolean(
    studentIdNum !== null &&
    studentQueryState.isSuccess &&
    studentQueryState.isFetchedAfterMount &&
    !studentQueryState.isFetching &&
    studentQueryState.fetchStatus !== 'paused' &&
    student?.is_visible === true,
  );

  const artworksQuery = useInfiniteQuery({
    queryKey: ['student-artworks', studentIdNum, galleryMode],
    queryFn: ({ pageParam = 1, signal }) =>
      studentService.getVisibleStudentArtworks(studentIdNum!, pageParam, studentArtworkPageSize, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    enabled: publicStudentVerified,
    retry: false,
    // Artist pages can be revisited from the browser cache after a visibility
    // change. Revalidate every mount/focus/reconnect before exposing the
    // cached artwork pages again.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
    networkMode: 'always',
  });

  const hasArtworkPageError = artworksQuery.isFetchNextPageError;
  const publicArtworksReady = publicStudentVerified && artworksQuery.isFetchedAfterMount && artworksQuery.data !== undefined &&
    artworksQuery.fetchStatus !== 'paused' && !artworksQuery.isRefetching && !artworksQuery.isRefetchError &&
    (artworksQuery.isSuccess || hasArtworkPageError);
  const artworks = useMemo(
    () => publicArtworksReady ? (artworksQuery.data?.pages.flatMap((page) => page.artworks) || []) : [],
    [artworksQuery.data, publicArtworksReady],
  );
  const artworksResponse = artworksQuery.data?.pages[0];
  const portfolioPaused = artworks.length === 0 && artworksQuery.fetchStatus === 'paused';

  useEffect(() => {
    if (!publicStudentVerified) {
      setLightboxOpen(false);
      setSelectedArtwork(null);
      setCurrentArtworkIndex(0);
      setArtworksForLightbox([]);
      setLightboxRouteId(null);
      lightboxOpenerRef.current = null;
    }
  }, [publicStudentVerified, studentIdNum]);

  // Lightbox handlers
  const openLightbox = (artwork: ArtworkResponse, activatingElement?: HTMLElement) => {
    if (!publicStudentVerified) {
      return;
    }
    const index = artworks.findIndex(a => a.artwork_id === artwork.artwork_id);
    lightboxOpenerRef.current = activatingElement ?? (document.activeElement as HTMLElement | null);
    setSelectedArtwork(artwork);
    setCurrentArtworkIndex(index);
    setArtworksForLightbox(artworks);
    setLightboxRouteId(studentIdNum);
    setLightboxOpen(true);
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setSelectedArtwork(null);
    setLightboxRouteId(null);
  }, []);

  useEffect(() => {
    if (!publicArtworksReady) closeLightbox();
  }, [closeLightbox, publicArtworksReady]);

  // Revalidation can reorder or replace loaded records while the viewer is
  // open. Keep the selected item, index, and count on one ordered loaded set.
  useEffect(() => {
    if (!lightboxOpen || lightboxRouteId !== studentIdNum || !selectedArtwork) return;
    const nextIndex = artworks.findIndex(item => item.artwork_id === selectedArtwork.artwork_id);
    if (nextIndex < 0) {
      closeLightbox();
      return;
    }
    if (artworksForLightbox !== artworks) setArtworksForLightbox(artworks);
    if (currentArtworkIndex !== nextIndex) setCurrentArtworkIndex(nextIndex);
    const nextArtwork = artworks[nextIndex];
    setSelectedArtwork(nextArtwork);
  }, [artworks, artworksForLightbox, closeLightbox, currentArtworkIndex, lightboxOpen, lightboxRouteId, publicArtworksReady, selectedArtwork, studentIdNum]);

  const navigateLightbox = (direction: 'prev' | 'next') => {
    const newIndex = direction === 'prev' ? currentArtworkIndex - 1 : currentArtworkIndex + 1;
    if (newIndex >= 0 && newIndex < artworksForLightbox.length) {
      setCurrentArtworkIndex(newIndex);
      setSelectedArtwork(artworksForLightbox[newIndex]);
    }
  };

  const handleBackClick = () => {
    const cameFromDirectory = (location.state as StudentArtworkLocationState | null)?.fromArtistsDirectory === true;
    if (cameFromDirectory) {
      navigate(-1);
    } else {
      navigate('/artists');
    }
  };

  if (!studentIdNum) {
    return (
      <PageContainer>
        <Header>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink to="/artists">Artists</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbCurrent>Invalid Student</BreadcrumbCurrent>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </Header>
        <InlineError 
          error={new Error('Invalid student ID provided')} 
          title="Invalid Student"
          onDismiss={() => navigate('/artists')}
        />
      </PageContainer>
    );
  }

  const studentApiError = studentError && typeof studentError === 'object' && 'type' in studentError
    ? studentError as unknown as ApiError
    : undefined;
  const artworkApiError = artworksQuery.error && typeof artworksQuery.error === 'object' && 'status' in artworksQuery.error
    ? artworksQuery.error as unknown as ApiError
    : undefined;
  const hiddenDuringArtworkLoad = artworkApiError?.status === 404;

  if (isLoadingStudent || studentQueryState.isFetching || studentQueryState.fetchStatus === 'paused' || !studentQueryState.isFetchedAfterMount) {
    return (
      <PageContainer>
        <Header>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink to="/artists">Artists</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbCurrent>Loading...</BreadcrumbCurrent>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </Header>
        <LoadingContainer>
          <LoadingSpinner text="Loading artist details..." />
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (isStudentError || !student || !publicStudentVerified || hiddenDuringArtworkLoad) {
    return (
      <PageContainer>
        <Header>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink to="/artists">Artists</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbCurrent>Artist unavailable</BreadcrumbCurrent>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </Header>
        
        <ErrorContainer>
          {/* Handle service outages */}
          {(studentApiError?.type === 'service_outage' || studentApiError?.type === 'network') ? (
            <ServiceOutageIndicator
              error={studentApiError}
              variant="card"
              onRetry={async () => { await refetchStudent(); }}
              onCheckStatus={refetchStudent}
            />
          ) : studentApiError?.isRetryable ? (
            <RetryableError
              error={studentApiError}
              onRetry={async () => {
                const result = await refetchStudent();
                if (result.isError) {
                  throw result.error;
                }
              }}
              variant="full"
              retryConfig={{
                maxAttempts: 3,
                initialDelay: 2000,
                enableAutoRetry: false,
              }}
            />
          ) : (
            <InlineError 
              error={hiddenDuringArtworkLoad
                ? new Error('This artist is no longer available')
                : studentApiError || new Error('Artist not found')}
              title={hiddenDuringArtworkLoad ? 'Artist Not Found' : 'Artist Unavailable'}
              onDismiss={() => navigate('/artists')}
            />
          )}
        </ErrorContainer>
      </PageContainer>
    );
  }

  const studentName = student.name;
  const artworkCount = student.artwork_count || 0;

  return (
    <ErrorBoundary>
      <PageContainer>
        <Header>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink to="/artists">Artists</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbCurrent>{studentName}</BreadcrumbCurrent>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <StudentInfo>
              <StudentDetails>
              <StudentName>{studentName}</StudentName>
              <StudentSubtitle>
                Student Artist
              </StudentSubtitle>
              <StudentStats>
                <ArtworkCountStat>
                  {artworkCount} {artworkCount === 1 ? (galleryMode ? 'work' : 'artwork') : (galleryMode ? 'works' : 'artworks')}
                </ArtworkCountStat>
              </StudentStats>
            </StudentDetails>
            <BackButton onClick={handleBackClick}>
              Back to Artists
            </BackButton>
          </StudentInfo>
        </Header>

        <GallerySection>
          <SectionTitle>Portfolio</SectionTitle>
          {portfolioPaused ? (
            <PortfolioPausedState role="status">
              <div>Portfolio is waiting for a network connection.</div>
              <PortfolioRetryButton type="button" onClick={() => void artworksQuery.refetch()}>
                Retry portfolio
              </PortfolioRetryButton>
            </PortfolioPausedState>
          ) : (
            <ArtworkGrid
              artworks={artworks}
              totalCount={artworksResponse?.total ?? 0}
              isLoading={artworksQuery.isLoading || (!publicArtworksReady && artworksQuery.isFetching)}
              isError={artworksQuery.isError || artworksQuery.isRefetchError}
              error={artworksQuery.error}
              onRetry={async () => { await artworksQuery.refetch(); }}
              hasNextPage={artworksQuery.hasNextPage}
              isFetchingNextPage={artworksQuery.isFetchingNextPage}
              isFetchNextPageError={artworksQuery.isFetchNextPageError}
              isFetching={artworksQuery.isFetching}
              onLoadMore={async () => { await artworksQuery.fetchNextPage(); }}
              columns={4}
              gap="24px"
              aspectRatio={galleryMode ? 1.2 : 1}
              onArtworkClick={openLightbox}
            />
          )}
        </GallerySection>

        <ArtworkLightbox
          artwork={lightboxRouteId === studentIdNum ? selectedArtwork : null}
          artworks={lightboxRouteId === studentIdNum ? artworksForLightbox : []}
          currentIndex={currentArtworkIndex}
          isOpen={lightboxOpen && lightboxRouteId === studentIdNum && publicStudentVerified}
          onClose={closeLightbox}
          onNavigate={navigateLightbox}
          galleryMode={galleryMode}
          activatingElement={lightboxOpenerRef.current}
          portfolioHref={studentIdNum ? `/artists/${studentIdNum}` : undefined}
        />
      </PageContainer>
    </ErrorBoundary>
  );
}
